import * as Yup from "yup"

import useIsDesktop from "@hooks/useIsDesktop"
import useRouter from "@hooks/useRouter"

import { AlertBanner, Button, NavigationLink } from "@atoms/index"

import { EmailInput, PasswordInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"

import styles from "@pages/Auth/Auth.module.scss"
import useAuth from "@pages/Auth/Auth.store"
import { emailSchema } from "@pages/Auth/AuthEmailCodeForm/AuthEmailCodeForm"

import { AUTH_ENDPOINTS } from "@endpoints/auth"

import {
    AuthEmailPasswordErrorResponse,
    EmailAndPasswordSchema,
    UserNameAndPasswordPayload,
} from "./AuthEmailPasswordForm.types"

const emailAndPasswordSchema: Yup.ObjectSchema<EmailAndPasswordSchema> = Yup.object()
    .concat(emailSchema)
    .shape({
        password: Yup.string().required("Please enter a password"),
    })

export default function AuthEmailPasswordForm() {
    const { authMethod, setAuthMethod, emailData, setEmailData, postAuthRedirectRoute } = useAuth()

    const isDesktop = useIsDesktop()
    const router = useRouter()

    const isActive = authMethod === "email_and_password"

    const onMutationSuccess = () => {
        router.replace(postAuthRedirectRoute)
    }

    const onRequestCodeInsteadClick = () => {
        void setAuthMethod("email_and_code")
    }

    const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailData({
            email: event.target.value,
            emailId: null,
        })
    }

    return (
        <GenericForm<EmailAndPasswordSchema, User, AuthEmailPasswordErrorResponse, UserNameAndPasswordPayload>
            formConfig={{
                defaultValues: {
                    email_address: emailData?.email ? emailData?.email : "",
                    password: "",
                },
            }}
            formValidationSchema={emailAndPasswordSchema}
            mutationConfig={{
                method: "POST",
                endpoint: AUTH_ENDPOINTS.PASSWORD.EMAIL_AUTHENTICATE,
                genericErrorMessage: "Could not log in.",
            }}
            triggerFormReset={!isActive}
            onMutationSuccess={onMutationSuccess}
            className={styles.form}
        >
            {({ isPending, nonFieldError }) => (
                <>
                    {nonFieldError && (
                        <AlertBanner size={isDesktop ? "sm" : "md"} title={nonFieldError} type="error" />
                    )}
                    <EmailInput<EmailAndPasswordSchema>
                        name="email_address"
                        label="Email address"
                        placeholder="email@mail.com"
                        isTabbable={isActive}
                        size={isDesktop ? "md" : "lg"}
                        onChangeCapture={onEmailChange}
                    />
                    <PasswordInput<EmailAndPasswordSchema>
                        name="password"
                        label="Password"
                        placeholder="Password"
                        isTabbable={isActive}
                        size={isDesktop ? "md" : "lg"}
                    />

                    <NavigationLink
                        onClick={onRequestCodeInsteadClick}
                        isUnderLine={true}
                        className={styles.link}
                        isTabbable={isActive}
                        title="Email and code form"
                        data-prevent-input-validation={true}
                    >
                        {() => "Use code instead"}
                    </NavigationLink>
                    <Button
                        type="submit"
                        colorScheme="gray"
                        size={isDesktop ? "md" : "lg"}
                        variant="solid"
                        isFullWidth={true}
                        isTabbable={isActive}
                        isLoading={isPending}
                    >
                        Sign in
                    </Button>
                </>
            )}
        </GenericForm>
    )
}
